/* eslint-disable max-len */
import { GetCountryNetworkResponse } from '@1po/1po-bff-fe-spec/generated/backoffice/country_network/response/GetCountryNetworkResponse';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { RootState } from 'app/AppStore';
import { FOUND, LOADING, SEARCH_STATUS } from 'utils';
import {
  COUNTRY_NETWORK_NAMESPACE,
  GET_COUNTRY_NETWORK_REQUEST,
  UPDATE_MKTP_RETURN_FEATURE_REQUEST,
  UPDATE_RD_RETURN_FEATURE_REQUEST,
} from './CountryNetwork.types';

export interface CountryNetworkState {
  country: string;
  isRdReturnsEnabled: boolean;
  isMktpReturnsEnabled: boolean;
  status: SEARCH_STATUS;
}

const initialState: CountryNetworkState = {
  country: '',
  isRdReturnsEnabled: false,
  isMktpReturnsEnabled: false,
  status: LOADING,
};

// Saga actions
export const getCountryNetworkRequestSaga = createAction(GET_COUNTRY_NETWORK_REQUEST);
export const updateRdReturnFeatureRequestSaga = createAction<{ isEnabled: boolean }>(UPDATE_RD_RETURN_FEATURE_REQUEST);
export const updateMktpReturnFeatureRequestSaga = createAction<{ isEnabled: boolean }>(
  UPDATE_MKTP_RETURN_FEATURE_REQUEST,
);

// Slice
const slice = createSlice({
  name: COUNTRY_NETWORK_NAMESPACE,
  initialState,
  reducers: {
    setCountryNetwork: (state, action: PayloadAction<GetCountryNetworkResponse>) => {
      state.country = action.payload.country;
      state.isRdReturnsEnabled = action.payload.isRdReturnsEnabled;
      state.isMktpReturnsEnabled = action.payload.isMktpReturnsEnabled;
      state.status = FOUND;
    },
    setRdReturnFeature: (state, action: PayloadAction<boolean>) => {
      state.isRdReturnsEnabled = action.payload;
    },
    setMktpReturnFeature: (state, action: PayloadAction<boolean>) => {
      state.isMktpReturnsEnabled = action.payload;
    },
    setCountryNetworkStatus: (state, action: PayloadAction<SEARCH_STATUS>) => {
      state.status = action.payload;
    },
    resetCountryNetwork: () => initialState,
  },
});

export const {
  setCountryNetwork,
  setCountryNetworkStatus,
  resetCountryNetwork,
  setRdReturnFeature,
  setMktpReturnFeature,
} = slice.actions;

// Saga actions
export const getCountryNetwork = createSelector(
  (state: RootState) => state.countryNetwork,
  (countryNetwork: CountryNetworkState) => ({
    returnFeature: {
      returnRdEnabled: countryNetwork.isRdReturnsEnabled,
      returnMktpEnabled: countryNetwork.isMktpReturnsEnabled,
    },
    country: countryNetwork.country,
    status: countryNetwork.status,
  }),
);

export const getCountryNetworkStatus = createSelector(
  (state: RootState) => state.countryNetwork.status,
  (status: SEARCH_STATUS) => status,
);

export const getIsRdReturnsEnabled = createSelector(
  (state: RootState) => state.countryNetwork.isRdReturnsEnabled,
  (isRdReturnsEnabled: boolean) => isRdReturnsEnabled,
);

export const getIsMktpReturnsEnabled = createSelector(
  (state: RootState) => state.countryNetwork.isMktpReturnsEnabled,
  (isMktpReturnsEnabled: boolean) => isMktpReturnsEnabled,
);

export const getCountry = createSelector(
  (state: RootState) => state.countryNetwork.country,
  (country: string) => country,
);

export default slice.reducer;
